// client/src/components/FiltersBar.jsx
import React, { useCallback, useState } from "react";
import { downloadOrdersCsv } from "../services/api";
import { formatEasternTime, getEasternTimeZoneLabel } from "../utils/timezone";

const FilterChip = ({ label, value, active, onClick }) => (
  <button
    onClick={() => onClick(active ? "" : value)}
    style={{
      padding: "8px 16px", borderRadius: 10, fontSize: 13,
      fontWeight: 600, cursor: "pointer", border: "none",
      background: active ? "#0f172a" : "white",
      color: active ? "white" : "#64748b",
      boxShadow: active ? "none" : "0 1px 3px rgba(0,0,0,0.08), inset 0 0 0 1.5px #e2e8f0",
      transition: "all 0.15s",
    }}
  >
    {label}
  </button>
);

const CARD_FILTER_LABELS = {
  should_cancel: "Should Cancel",
  mismatch: "Mismatches",
  location_ok: "Location OK",
  validated: "Validated",
  open_shipcube: "Open · Ship Cube",
  open_mismatch: "Open · Wrong Location",
  protected_cancelled: "Protected (Cancelled by ShipGuard)",
  shipped_mismatch: "Shipped from Wrong Location",
  fulfilled_clean: "Fulfilled · Clean",
  location_disabled: "Location Disabled",
};

const FiltersBar = ({
  filters, onFilterChange, totalCount, loading, onRefresh, lastFetch,
  autoRefresh, onToggleAutoRefresh, cardFilter, onClearCardFilter, exportParams,
  statusOptions,
}) => {
  const shipHeroFulfillmentFilters = [
    { label: "All", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Fulfilled", value: "fulfilled" },
    { label: "Location Disabled", value: "location_disabled" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Cancelled & Loc Changed", value: "cancelled_location_changed" },
    { label: "Partial & Loc Changed", value: "partial_fulfilled_location_changed" },
  ];

  const pendingFilters = [
    { label: "All", value: "" },
    { label: "Pending Only", value: "1" },
  ];

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    const params = {};
    if (exportParams?.filter) params.filter = exportParams.filter;
    if (exportParams?.search) params.search = exportParams.search;
    if (exportParams?.sort) params.sort = exportParams.sort;
    if (exportParams?.dir) params.dir = exportParams.dir;
    if (exportParams?.shiphero_fulfillment_status) params.shiphero_fulfillment_status = exportParams.shiphero_fulfillment_status;
    if (exportParams?.pending_only) params.pending_only = exportParams.pending_only;
    if (exportParams?.location) params.location = exportParams.location;
    if (exportParams?.date_from) params.date_from = exportParams.date_from;
    if (exportParams?.date_to) params.date_to = exportParams.date_to;
    if (exportParams?.history_action) params.history_action = exportParams.history_action;
    if (exportParams?.history_note) params.history_note = exportParams.history_note;
    setExporting(true);
    try {
      await downloadOrdersCsv(params);
    } catch (err) {
      console.error("[Export] Failed:", err);
      alert("Export failed: " + (err.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  }, [exportParams]);

  return (
    <div style={{ background: "white", borderRadius: 14, border: "1px solid #e8ecf0", padding: "16px 20px", marginBottom: 20 }}>
      {/* Active card filter banner */}
      {cardFilter && CARD_FILTER_LABELS[cardFilter] && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 14px", marginBottom: 12, borderRadius: 10,
          background: "#fef3c7", border: "1.5px solid #fbbf24",
        }}>
          <span style={{ fontSize: 13 }}>🔍</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
            Filtered by: {CARD_FILTER_LABELS[cardFilter]}
          </span>
          <button
            onClick={onClearCardFilter}
            style={{
              marginLeft: "auto", padding: "4px 12px", borderRadius: 8,
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              border: "1.5px solid #d97706", background: "white", color: "#d97706",
            }}
          >
            ✕ Clear Filter
          </button>
        </div>
      )}

      {/* Top row - search + refresh + export */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <span style={{
            position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
            fontSize: 15, pointerEvents: "none",
          }}>🔍</span>
          <input
            placeholder="Search by order #, customer, email..."
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            style={{
              width: "100%", padding: "10px 14px 10px 40px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 14, outline: "none", color: "#1e293b",
              fontFamily: "inherit", boxSizing: "border-box",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#0ea5e9")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>

        {lastFetch && (
          <span style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>
            Updated {formatEasternTime(lastFetch)} {getEasternTimeZoneLabel()}
          </span>
        )}

        <button
          onClick={handleExport}
          disabled={exporting}
          title="Export current view as CSV with item-level data"
          style={{
            padding: "9px 14px", borderRadius: 10, fontSize: 12,
            fontWeight: 600, cursor: exporting ? "not-allowed" : "pointer",
            border: "1.5px solid #10b981",
            background: exporting ? "#f0fdf4" : "white", color: "#059669",
            whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5,
          }}
        >
          {exporting ? "⏳ Exporting..." : "📄 Export CSV"}
        </button>

        <button
          onClick={() => onToggleAutoRefresh(!autoRefresh)}
          title="Toggle auto-refresh every 60s"
          style={{
            padding: "9px 14px", borderRadius: 10, fontSize: 12,
            fontWeight: 600, cursor: "pointer", border: "1.5px solid #e2e8f0",
            background: autoRefresh ? "#dbeafe" : "white",
            color: autoRefresh ? "#1d4ed8" : "#64748b",
            whiteSpace: "nowrap",
          }}
        >
          {autoRefresh ? "⏸ Auto" : "▶ Auto"}
        </button>

        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            padding: "9px 20px", borderRadius: 10, fontSize: 13,
            fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
            border: "none",
            background: loading ? "#f1f5f9" : "#0f172a",
            color: loading ? "#94a3b8" : "white",
            display: "flex", alignItems: "center", gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ display: "inline-block", animation: loading ? "spin 1s linear infinite" : "none" }}>↻</span>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Filter rows */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            Date Range
          </span>
          <input
            type="date"
            value={filters.date_from || ""}
            onChange={(e) => onFilterChange("date_from", e.target.value)}
            style={{
              width: 140, padding: "8px 10px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, outline: "none", color: "#1e293b",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
          <span style={{ fontSize: 12, color: "#94a3b8" }}>to</span>
          <input
            type="date"
            value={filters.date_to || ""}
            onChange={(e) => onFilterChange("date_to", e.target.value)}
            style={{
              width: 140, padding: "8px 10px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, outline: "none", color: "#1e293b",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ width: 1, height: 30, background: "#e2e8f0", flexShrink: 0 }} />

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            Location
          </span>
          <input
            placeholder="Filter by location name or ID..."
            value={filters.location || ""}
            onChange={(e) => onFilterChange("location", e.target.value)}
            style={{
              width: 240, padding: "8px 12px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, outline: "none", color: "#1e293b",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
          {/* Real statuses present at this location (DRMTLGY etc.) — the
              chips below only know the fixed composite buckets */}
          <select
            value={filters.shiphero_fulfillment_status || ""}
            onChange={(e) => onFilterChange("shiphero_fulfillment_status", e.target.value)}
            title="Fulfillment statuses present in the current location/date window"
            style={{
              width: 190, padding: "8px 10px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, outline: "none",
              color: filters.shiphero_fulfillment_status ? "#0f172a" : "#64748b",
              fontFamily: "inherit", boxSizing: "border-box",
              background: "white", cursor: "pointer",
            }}
          >
            <option value="">Status: all</option>
            {(statusOptions || []).map((s) => (
              <option key={s.status} value={s.status}>
                {s.status} ({s.cnt.toLocaleString()})
              </option>
            ))}
          </select>
        </div>

        <div style={{ width: 1, height: 30, background: "#e2e8f0", flexShrink: 0 }} />

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            SH Fulfillment
          </span>
          <div style={{ display: "flex", gap: 5 }}>
            {shipHeroFulfillmentFilters.map((f) => (
              <FilterChip
                key={f.value}
                label={f.label}
                value={f.value}
                active={filters.shiphero_fulfillment_status === f.value}
                onClick={(v) => onFilterChange("shiphero_fulfillment_status", v)}
              />
            ))}
          </div>
        </div>

        <div style={{ width: 1, height: 30, background: "#e2e8f0", flexShrink: 0 }} />

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            Pending
          </span>
          <div style={{ display: "flex", gap: 5 }}>
            {pendingFilters.map((f) => (
              <FilterChip
                key={f.value}
                label={f.label}
                value={f.value}
                active={(filters.pending_only || "") === f.value}
                onClick={(v) => onFilterChange("pending_only", v)}
              />
            ))}
          </div>
        </div>

        <div style={{ width: 1, height: 30, background: "#e2e8f0", flexShrink: 0 }} />

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            SH History
          </span>
          <select
            value={filters.history_action || ""}
            onChange={(e) => onFilterChange("history_action", e.target.value)}
            title="Filter by who actioned the order, from ShipHero order-history notes (populated by the history scan)"
            style={{
              width: 210, padding: "8px 10px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, outline: "none",
              color: filters.history_action ? "#0f172a" : "#64748b",
              fontFamily: "inherit", boxSizing: "border-box",
              background: "white", cursor: "pointer",
            }}
          >
            <option value="">Action: all</option>
            <option value="sg_cancelled">Cancelled by ShipGuard</option>
            <option value="sg_only">ShipGuard only (no SH automation)</option>
            <option value="auto_cancelled">Cancelled by SH automation</option>
            <option value="loc_disabled_close">SH closed (locations disabled)</option>
            <option value="unscanned">History not scanned yet</option>
          </select>
          <input
            placeholder="History note contains..."
            value={filters.history_note || ""}
            onChange={(e) => onFilterChange("history_note", e.target.value)}
            title='Free-text match over stored ShipHero history notes, e.g. "automation rule" or "Operator Hold"'
            style={{
              width: 200, padding: "8px 12px",
              border: "1.5px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, outline: "none", color: "#1e293b",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
        Source: <strong>ShipHero</strong> fulfillment status. Location values come from <strong>Shopify</strong> validation.
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default FiltersBar;
